"use client";

import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { NoticeView } from "@/lib/data/notices";

interface ServiceNoticeProps {
  notice: NoticeView;
  closeLabel: string;
  viewMoreLabel: string;
}

export function ServiceNotice({
  notice,
  closeLabel,
  viewMoreLabel,
}: ServiceNoticeProps) {
  const [open, setOpen] = useState(false);

  // The notice stays visible throughout its configured period —
  // it appears on every visit; closing only hides the current view.
  useEffect(() => {
    const timer = setTimeout(() => setOpen(true), 0);
    return () => clearTimeout(timer);
  }, [notice.id]);

  const dismiss = () => setOpen(false);

  if (!open) return null;

  const body = (
    <>
      <button
        type="button"
        onClick={dismiss}
        aria-label={closeLabel}
        className="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-full text-muted transition-colors hover:bg-canvas hover:text-strong"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden>
          <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.3" />
        </svg>
      </button>
      {notice.imageUrl && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={notice.imageUrl}
          alt=""
          className="block max-h-64 w-full object-cover"
        />
      )}
      <div className="p-7 pt-6 sm:p-9 sm:pt-7">
        <h2 className="font-display text-xl font-medium tracking-[-0.01em] text-strong">
          {notice.title}
        </h2>
        <div className="notice-md mt-3 max-h-60 overflow-y-auto text-sm leading-loose text-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{notice.content}</ReactMarkdown>
        </div>
        {notice.linkUrl && (
          <a
            href={notice.linkUrl}
            target={notice.linkUrl.startsWith("http") ? "_blank" : undefined}
            rel={notice.linkUrl.startsWith("http") ? "noopener noreferrer" : undefined}
            onClick={dismiss}
            className="btn-dark mt-6 w-full !py-2.5 text-center text-xs"
          >
            {viewMoreLabel}
          </a>
        )}
      </div>
    </>
  );

  if (notice.popupType === "BANNER") {
    return (
      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-line bg-paper/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-5 py-4 sm:px-8">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-strong">
              {notice.title}
            </p>
            <p className="mt-0.5 truncate text-xs text-muted line-clamp-1">
              {notice.content}
            </p>
          </div>
          {notice.linkUrl && (
            <a
              href={notice.linkUrl}
              target={notice.linkUrl.startsWith("http") ? "_blank" : undefined}
              rel={notice.linkUrl.startsWith("http") ? "noopener noreferrer" : undefined}
              onClick={dismiss}
              className="shrink-0 text-xs font-medium tracking-[0.14em] text-accent-deep uppercase transition-colors hover:text-strong"
            >
              {viewMoreLabel}
            </a>
          )}
          <button
            type="button"
            onClick={dismiss}
            aria-label={closeLabel}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted transition-colors hover:bg-canvas hover:text-strong"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden>
              <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.3" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  if (notice.popupType === "BOTTOM_SHEET") {
    return (
      <div className="fixed inset-x-0 bottom-0 z-50 p-4 sm:p-6">
        <div
          style={{ maxWidth: notice.modalWidth }}
          className="relative mx-auto w-full max-w-lg overflow-hidden rounded-lg border border-line bg-paper shadow-[0_-16px_60px_-24px_rgba(35,33,29,0.35)]"
        >
          {body}
        </div>
      </div>
    );
  }

  // MODAL (default)
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={notice.title}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-strong/40 p-5 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) dismiss();
      }}
    >
      <div
        style={{ maxWidth: notice.modalWidth }}
        className="relative w-full max-w-md overflow-hidden rounded-lg border border-line bg-paper shadow-[0_28px_70px_-30px_rgba(35,33,29,0.45)]"
      >
        {body}
      </div>
    </div>
  );
}
